// src/pages/Signup.js
// Apple Dark Mode Style with pendingGroup handling
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Auth.css';
import API_URL from '../config';

function Signup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [pendingGroup, setPendingGroup] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Check for pending group from wizard
  useEffect(() => {
    const pending = localStorage.getItem('pendingGroup');
    if (pending) {
      try {
        setPendingGroup(JSON.parse(pending));
      } catch (e) {
        localStorage.removeItem('pendingGroup');
      }
    }
  }, []);

  // Create pending group after successful signup
  const createPendingGroup = async (token) => {
    if (!pendingGroup) return null;

    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const response = await fetch(`${API_URL}/api/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          groupName: pendingGroup.groupName,
          meetingFrequency: { 
            dayOfWeek: pendingGroup.meetingDay, 
            time: pendingGroup.meetingTime 
          },
          frequency: pendingGroup.frequency === 'weekly' ? 'Varje vecka' : 'Varannan vecka',
          participants: pendingGroup.emails.map(email => ({ 
            email, 
            name: email.split('@')[0] 
          })),
          threshold: Math.ceil(pendingGroup.emails.length * 0.5) || 1,
          timezone: userTimezone,
          location: '',
          invitationAdvance: 7,
          reminderAdvance: 3,
          decisionTime: '18:00'
        })
      });

      if (response.ok) {
        const newGroup = await response.json();
        localStorage.removeItem('pendingGroup');
        window.dispatchEvent(new Event('groupsUpdated'));
        return newGroup;
      }
    } catch (error) {
      console.error('Error creating pending group:', error);
    }
    
    localStorage.removeItem('pendingGroup');
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password !== confirmPassword) {
      setError(t('signup.errors.passwordMismatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('signup.errors.passwordTooShort'));
      return;
    }

    if (!acceptedTerms) {
      setError(t('signup.errors.acceptTerms'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('signup.errors.signupFailed'));
      }

      // Save token and user data
      localStorage.setItem('token', data.token);
      localStorage.setItem('userEmail', data.user.email);
      localStorage.setItem('userId', data.user.id);
      localStorage.setItem('userPlan', data.user.accountType);
      
      // Check for pending group from wizard
      const createdGroup = await createPendingGroup(data.token);
      
      if (createdGroup) {
        // Redirect to the newly created group
        navigate(`/groups/${createdGroup._id}`);
      } else {
        // Redirect to dashboard
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Password visibility toggle icon
  const EyeIcon = ({ visible }) => visible ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>{t('signup.title')}</h1>
        <p className="auth-subtitle">
          {pendingGroup 
            ? t('signup.subtitleWithGroup')
            : t('signup.subtitle')
          }
        </p>

        {/* Pending Group Notice */}
        {pendingGroup && (
          <div className="pending-group-notice">
            <p>
              <strong>"{pendingGroup.groupName}"</strong>
              {' '}{t('signup.pendingGroupReady')}
              {pendingGroup.emails.length > 0 && (
                <span style={{ display: 'block', marginTop: '4px', opacity: 0.8 }}>
                  {pendingGroup.emails.length} {t('signup.participantsWaiting')}
                </span>
              )}
            </p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">{t('signup.emailLabel')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('signup.emailPlaceholder')}
              autoComplete="email"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('signup.passwordLabel')}</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder={t('signup.passwordPlaceholder')}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t('signup.hidePassword') : t('signup.showPassword')}
              >
                <EyeIcon visible={showPassword} />
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t('signup.confirmPasswordLabel')}</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder={t('signup.confirmPasswordPlaceholder')}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? t('signup.hidePassword') : t('signup.showPassword')}
              >
                <EyeIcon visible={showConfirmPassword} />
              </button>
            </div>
          </div>

          {/* Terms Checkbox */}
          <div className="auth-checkbox">
            <input
              type="checkbox"
              id="terms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
            />
            <label htmlFor="terms">
              {t('signup.acceptTerms')}{' '}
              <Link to="/terms">{t('signup.termsLink')}</Link>
              {' '}{t('signup.and')}{' '}
              <Link to="/privacy">{t('signup.privacyLink')}</Link>
            </label>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button 
            type="submit" 
            className={`auth-button ${loading ? 'loading' : ''}`}
            disabled={loading}
          >
            {loading 
              ? t('signup.creatingAccount') 
              : pendingGroup 
                ? t('signup.createAndSave')
                : t('signup.submitButton')
            }
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {t('signup.hasAccount')}{' '}
            <Link to="/login">{t('signup.loginLink')}</Link>
          </p>
        </div>

        {/* Plan Info - Only show if no pending group */}
        {!pendingGroup && (
          <div className="plan-info">
            <h3>{t('signup.freePlan.title')}</h3>
            <ul>
              <li>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {t('signup.freePlan.feature1')}
              </li>
              <li>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {t('signup.freePlan.feature2')}
              </li>
              <li>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {t('signup.freePlan.feature3')}
              </li>
              <li>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {t('signup.freePlan.feature4')}
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default Signup;
